'use client'

import { Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { cn } from '@/lib/utils'
import useIsHydrated from '@/utils/hooks/useIsHydrated'

interface ThemeToggleProps {
	className?: string
}

export function ThemeToggleIcon({ className }: ThemeToggleProps) {
	const { resolvedTheme, setTheme } = useTheme()
	const mounted = useIsHydrated()

	const isDark = mounted ? resolvedTheme === 'dark' : true

	return (
		<button
			type="button"
			data-testid="theme-toggle-icon"
			onClick={() => setTheme(isDark ? 'light' : 'dark')}
			aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
			className={cn(
				'relative inline-flex size-9 items-center justify-center rounded-full border transition-colors',
				'border-border bg-transparent hover:bg-accent hover:text-accent-foreground',
				className
			)}
		>
			{mounted && isDark ? (
				<Moon className="size-4" strokeWidth={1.75} />
			) : (
				<Sun className="size-4" strokeWidth={1.75} />
			)}
		</button>
	)
}

export function ThemeToggle({ className }: ThemeToggleProps) {
	const { resolvedTheme, setTheme } = useTheme()
	const mounted = useIsHydrated()

	const isDark = mounted ? resolvedTheme === 'dark' : true

	const toggle = () => {
		setTheme(isDark ? 'light' : 'dark')
	}

	const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			toggle()
		}
	}

	return (
		<div
			className={cn(
				'flex w-16 h-8 p-1 rounded-full cursor-pointer transition-colors duration-300',
				isDark ? 'bg-zinc-950 border border-zinc-800' : 'bg-white border border-zinc-200',
				className
			)}
			onClick={toggle}
			onKeyDown={onKeyDown}
			role="switch"
			aria-checked={isDark}
			aria-label="Toggle color theme"
			tabIndex={0}
		>
			<div className="flex justify-between items-center w-full">
				<div
					className={cn(
						'flex justify-center items-center size-6 rounded-full transition-transform duration-300',
						isDark ? 'transform translate-x-0 bg-zinc-800' : 'transform translate-x-8 bg-zinc-200'
					)}
				>
					{isDark ? (
						<Moon className="size-4 text-foreground" strokeWidth={1.5} />
					) : (
						<Sun className="size-4 text-zinc-700" strokeWidth={1.5} />
					)}
				</div>
				<div
					className={cn(
						'flex justify-center items-center size-6 rounded-full transition-transform duration-300',
						isDark ? 'bg-transparent' : 'transform -translate-x-8'
					)}
				>
					{isDark ? (
						<Sun className="size-4 text-zinc-500" strokeWidth={1.5} />
					) : (
						<Moon className="size-4 text-black" strokeWidth={1.5} />
					)}
				</div>
			</div>
		</div>
	)
}
